import type { FontFamilyId, ThemePreset } from "./types";

/**
 * Curated font-family stacks. Only these are offered in the UI; arbitrary
 * web-font loading is deferred (tdd-0045 open decision #1). The `sans` / `serif`
 * / `mono` ids map to the design-system tokens already loaded app-wide, so they
 * cost nothing extra to use.
 */
export const FONT_STACKS: Record<FontFamilyId, string> = {
  sans: "var(--font-sans)",
  serif: "var(--font-serif)",
  mono: "var(--font-mono)",
  system:
    'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  geist: "var(--font-sans)", // Geist is wired to --font-sans in docs layout
  inter: "Inter, var(--font-sans)",
  "source-serif": '"Source Serif 4", var(--font-serif)',
  "ibm-plex-mono": '"IBM Plex Mono", var(--font-mono)',
  // OS-installed stacks (macOS / Windows) — broaden the classic/corporate
  // pairings the way Word & Google Docs do, with no web-font loading.
  georgia: 'Georgia, "Times New Roman", serif',
  times: '"Times New Roman", Times, "Liberation Serif", serif',
  garamond: '"EB Garamond", Garamond, "Apple Garamond", Georgia, serif',
  trebuchet: '"Trebuchet MS", "Segoe UI", Tahoma, sans-serif',
  verdana: "Verdana, Geneva, Tahoma, sans-serif",
  tahoma: 'Tahoma, "Segoe UI", Verdana, sans-serif',
};

export const FONT_LABELS: Record<FontFamilyId, string> = {
  sans: "Sans (default)",
  serif: "Serif",
  mono: "Monospace",
  system: "System UI",
  geist: "Geist",
  inter: "Inter",
  "source-serif": "Source Serif",
  "ibm-plex-mono": "IBM Plex Mono",
  georgia: "Georgia",
  times: "Times New Roman",
  garamond: "Garamond",
  trebuchet: "Trebuchet MS",
  verdana: "Verdana",
  tahoma: "Tahoma",
};

export const DEFAULT_PRESET_ID = "default";

/**
 * The "default" preset is pinned to today's look (matches the hardcoded values
 * in `@workspace/markdown/doc-typography.css` / globals.css) so an unthemed document renders
 * byte-identically until a theme is explicitly chosen.
 *
 *   --text-h1 1.5rem … --text-body 1rem on a 16px base ⇒ fontScale ≈ 1.085
 *   per step (h1 is +4 steps over body). Heading weight 600, body 400.
 */
export const PRESETS: Record<string, ThemePreset> = {
  default: {
    id: "default",
    name: "Default",
    description: "The standard docs look — Geist sans, balanced spacing.",
    base: {
      accent: "var(--primary)",
      bodyColor: "var(--foreground)",
      background: "var(--background)",
      fontBody: "sans",
      fontHeading: "sans",
      fontMono: "mono",
      bodyWeight: 400,
      headingWeight: 600,
      italicHeadings: false,
      baseFontSize: 16,
      fontScale: 1.085,
      spacing: 1,
    },
    // Pins today's exact (irregular) scale so an unthemed doc is byte-identical.
    // Used only until the user touches the base-size / font-scale knobs.
    fixedSizesRem: {
      h1: 1.5,
      h2: 1.33,
      h3: 1.25,
      h4: 1.125,
      h5: 1,
      h6: 1,
      body: 1,
    },
  },

  // ── Modern · sans-serif ────────────────────────────────────────────────────
  modern: {
    id: "modern",
    name: "Modern",
    description: "Clean Inter sans with a soft blue accent — crisp and current.",
    base: {
      accent: "oklch(0.58 0.16 250)",
      bodyColor: "oklch(0.28 0.01 260)",
      background: "oklch(0.99 0.002 260)",
      fontBody: "inter",
      fontHeading: "inter",
      fontMono: "ibm-plex-mono",
      bodyWeight: 400,
      headingWeight: 600,
      italicHeadings: false,
      baseFontSize: 16,
      fontScale: 1.18,
      spacing: 1.05,
    },
  },

  geometric: {
    id: "geometric",
    name: "Geometric",
    description: "Trebuchet headings, airy spacing, teal accent — friendly and modern.",
    base: {
      accent: "oklch(0.6 0.12 195)",
      bodyColor: "oklch(0.3 0.01 250)",
      background: "oklch(0.99 0.003 220)",
      fontBody: "verdana",
      fontHeading: "trebuchet",
      fontMono: "mono",
      bodyWeight: 400,
      headingWeight: 700,
      italicHeadings: false,
      baseFontSize: 16,
      fontScale: 1.2,
      spacing: 1.15,
    },
  },

  // ── Classic · serif ────────────────────────────────────────────────────────
  classic: {
    id: "classic",
    name: "Classic",
    description: "Georgia throughout with a muted burgundy accent — timeless report style.",
    base: {
      accent: "oklch(0.45 0.13 25)",
      bodyColor: "oklch(0.25 0.01 60)",
      background: "oklch(0.985 0.006 85)",
      fontBody: "georgia",
      fontHeading: "georgia",
      fontMono: "ibm-plex-mono",
      bodyWeight: 400,
      headingWeight: 700,
      italicHeadings: false,
      baseFontSize: 17,
      fontScale: 1.2,
      spacing: 1.15,
    },
  },

  editorial: {
    id: "editorial",
    name: "Editorial",
    description: "Garamond body with italic headings, generous spacing — long-form reading.",
    base: {
      accent: "oklch(0.42 0.06 145)",
      bodyColor: "oklch(0.24 0.005 80)",
      background: "oklch(0.98 0.008 85)",
      fontBody: "garamond",
      fontHeading: "garamond",
      fontMono: "ibm-plex-mono",
      bodyWeight: 400,
      headingWeight: 600,
      italicHeadings: true,
      baseFontSize: 18,
      fontScale: 1.25,
      spacing: 1.3,
    },
  },

  manuscript: {
    id: "manuscript",
    name: "Manuscript",
    description: "Times body, sans headings — the academic / legal document pairing.",
    base: {
      accent: "oklch(0.4 0.04 250)",
      bodyColor: "oklch(0.22 0 0)",
      background: "oklch(0.995 0 0)",
      fontBody: "times",
      fontHeading: "system",
      fontMono: "mono",
      bodyWeight: 400,
      headingWeight: 700,
      italicHeadings: false,
      baseFontSize: 17,
      fontScale: 1.15,
      spacing: 1.1,
    },
  },

  // ── Conservative · neutral palettes ──────────────────────────────────────────
  monochrome: {
    id: "monochrome",
    name: "Monochrome",
    description: "Pure black on white, system sans — maximally neutral and printable.",
    base: {
      accent: "oklch(0.2 0 0)",
      bodyColor: "oklch(0.18 0 0)",
      background: "oklch(1 0 0)",
      fontBody: "system",
      fontHeading: "system",
      fontMono: "mono",
      bodyWeight: 400,
      headingWeight: 700,
      italicHeadings: false,
      baseFontSize: 16,
      fontScale: 1.18,
      spacing: 1.05,
    },
  },

  corporate: {
    id: "corporate",
    name: "Corporate",
    description: "Conservative navy accent, Tahoma — the classic business-document look.",
    base: {
      accent: "oklch(0.42 0.15 255)",
      bodyColor: "oklch(0.26 0.01 255)",
      background: "oklch(0.99 0.002 255)",
      fontBody: "tahoma",
      fontHeading: "tahoma",
      fontMono: "mono",
      bodyWeight: 400,
      headingWeight: 700,
      italicHeadings: false,
      baseFontSize: 15.5,
      fontScale: 1.15,
      spacing: 1,
    },
  },

  // ── Vibrant · high-contrast ──────────────────────────────────────────────────
  vivid: {
    id: "vivid",
    name: "Vivid",
    description: "Bold magenta accent and heavy headings — make it pop.",
    base: {
      accent: "oklch(0.58 0.24 350)",
      bodyColor: "oklch(0.25 0.01 320)",
      background: "oklch(0.99 0.003 320)",
      fontBody: "inter",
      fontHeading: "inter",
      fontMono: "ibm-plex-mono",
      bodyWeight: 400,
      headingWeight: 800,
      italicHeadings: false,
      baseFontSize: 16,
      fontScale: 1.22,
      spacing: 1.1,
    },
  },

  contrast: {
    id: "contrast",
    name: "High contrast",
    description: "Near-black body, vivid emerald accent, heavy weights — maximum legibility.",
    base: {
      accent: "oklch(0.55 0.16 155)",
      bodyColor: "oklch(0.15 0 0)",
      background: "oklch(1 0 0)",
      fontBody: "system",
      fontHeading: "system",
      fontMono: "mono",
      bodyWeight: 500,
      headingWeight: 800,
      italicHeadings: false,
      baseFontSize: 17,
      fontScale: 1.2,
      spacing: 1.1,
    },
  },

  // ── Utility ──────────────────────────────────────────────────────────────────
  compact: {
    id: "compact",
    name: "Compact",
    description: "Smaller base size and tight spacing — fit more on screen.",
    base: {
      accent: "var(--primary)",
      bodyColor: "var(--foreground)",
      background: "var(--background)",
      fontBody: "sans",
      fontHeading: "sans",
      fontMono: "mono",
      bodyWeight: 400,
      headingWeight: 600,
      italicHeadings: false,
      baseFontSize: 14,
      fontScale: 1.1,
      spacing: 0.7,
    },
  },
};

export function getPreset(id: string): ThemePreset {
  return PRESETS[id] ?? PRESETS[DEFAULT_PRESET_ID];
}

export const PRESET_LIST: ThemePreset[] = Object.values(PRESETS);
